import { Alert, Snackbar, SnackbarOrigin } from '@mui/material';
import { useState, useCallback } from 'react';

import type { ReactNode } from 'react';

interface ToastState {
  open: boolean;
  message: ReactNode;
  severity: 'success' | 'info' | 'warning' | 'error';
  anchorOrigin?: SnackbarOrigin | undefined;
  autoHideDuration?: number;
}

export const useToast = () => {
  const [toastState, setToastState] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
    anchorOrigin: { vertical: 'top', horizontal: 'center' },
    autoHideDuration: 5000,
  });

  const showToast = useCallback(
    (
      message: ReactNode,
      severity: 'success' | 'info' | 'warning' | 'error' = 'info',
      anchorOrigin: SnackbarOrigin = {
        vertical: 'top',
        horizontal: 'center',
      },
      autoHideDuration: number = 5000,
    ) => {
      setToastState({
        open: true,
        message,
        severity,
        anchorOrigin,
        autoHideDuration,
      });
    },
    [],
  );

  const hideToast = useCallback(() => {
    setToastState(prev => ({ ...prev, open: false }));
  }, []);

  const ToastComponent = () => (
    <Snackbar
      open={toastState.open}
      autoHideDuration={toastState.autoHideDuration}
      onClose={hideToast}
      anchorOrigin={toastState.anchorOrigin}
    >
      <Alert
        onClose={hideToast}
        severity={toastState.severity}
        variant="standard"
        sx={theme => ({
          width: '100%',
          ...(toastState.severity === 'error' && {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
            borderColor: theme.palette.error.dark,

            '& .MuiAlert-icon': {
              color: theme.palette.error.contrastText,
            },

            '& svg': {
              color: theme.palette.error.contrastText,
              fill: theme.palette.error.contrastText,
            },

            '& .MuiAlert-action svg': {
              color: theme.palette.error.contrastText,
              fill: theme.palette.error.contrastText,
            },
          }),
        })}
      >
        {toastState.message}
      </Alert>
    </Snackbar>
  );

  return { showToast, ToastComponent };
};
